import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ChevronRight, ChevronLeft, CheckCircle2, Upload, X, Briefcase, AlertCircle, FileText } from "lucide-react";
import { applyBrandColor } from "../../contexts/AgencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "intro", label: "Welcome" },
  { key: "personal", label: "About You" },
  { key: "qualifications", label: "Qualifications" },
  { key: "availability", label: "Availability" },
  { key: "documents", label: "Documents" },
  { key: "review", label: "Review" },
];

const DOC_TYPES = [
  { key: "resume", label: "Resume", required: false },
  { key: "drivers_license", label: "Driver's License", required: false },
  { key: "state_id", label: "State ID", required: false },
  { key: "ssn_card", label: "Social Security Card", required: false },
  { key: "tb_clearance", label: "TB Clearance", required: false },
  { key: "cpr", label: "CPR Certification", required: false },
  { key: "cna", label: "CNA Certification", required: false },
  { key: "guardian_registration", label: "Guardian Registration", required: false },
  { key: "vehicle_insurance", label: "Vehicle Insurance", required: false },
  { key: "vehicle_registration", label: "Vehicle Registration", required: false },
  { key: "other", label: "Other Documents", required: false },
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Apply() {
  const { agencyId } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null); // { id, tracking_code }
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    address: "", city: "", state: "", zip_code: "",
    position_applied_for: "Caregiver",
    has_drivers_license: null,
    has_reliable_transportation: null,
    has_cna_certification: null,
    has_cpr_certification: null,
    has_tb_clearance: null,
    years_of_experience: "",
    availability_days: [],
    availability_shift: "",
    earliest_start_date: "",
    previous_employer: "",
    why_home_care: "",
    additional_info: "",
  });

  useEffect(() => {
    axios.get(`${API}/agencies/${agencyId}/public`)
      .then((r) => {
        setAgency(r.data);
        applyBrandColor(r.data.brand_primary_color);
      })
      .catch(() => setError("This agency is not available."))
      .finally(() => setLoading(false));
  }, [agencyId]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleDay = (day) => {
    setData((d) => {
      const days = d.availability_days.includes(day)
        ? d.availability_days.filter((x) => x !== day)
        : [...d.availability_days, day];
      return { ...d, availability_days: days };
    });
  };

  const validateStep = () => {
    if (stepIdx === 1) {
      if (!data.first_name || !data.last_name || !data.email || !data.phone) {
        setError("Please fill in your name, email, and phone.");
        return false;
      }
    }
    setError(null);
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data: res } = await axios.post(`${API}/hiring/applications/public`, {
        ...data,
        agency_id: agencyId,
      });
      setSubmission(res);
      setStepIdx(STEPS.length); // success step
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error && !agency) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="surface p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="font-semibold text-xl">Agency not found</h2>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Success screen with document upload
  if (submission) {
    return <SuccessAndUpload agency={agency} submission={submission} firstName={data.first_name} />;
  }

  const step = STEPS[stepIdx];

  return (
    <div className="min-h-screen bg-[#F3F4F6] py-6 md:py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Agency header */}
        <header className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
            <Briefcase className="w-3.5 h-3.5" />
            Career Application
          </div>
          <h1 className="heading-display">{agency.name}</h1>
          {agency.address && <p className="text-sm text-muted-foreground mt-1">{agency.address}</p>}
        </header>

        {/* Stepper */}
        <Stepper currentIdx={stepIdx} />

        {/* Card */}
        <div className="surface p-6 md:p-8 mt-5">
          {step.key === "intro" && <Intro agency={agency} />}
          {step.key === "personal" && <PersonalStep data={data} set={set} />}
          {step.key === "qualifications" && <QualificationsStep data={data} set={set} />}
          {step.key === "availability" && <AvailabilityStep data={data} set={set} toggleDay={toggleDay} />}
          {step.key === "documents" && <DocumentsInfoStep />}
          {step.key === "review" && <ReviewStep data={data} />}

          {error && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="apply-error">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={prev}
              disabled={stepIdx === 0}
              className="btn-ghost disabled:opacity-30"
              data-testid="apply-prev"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button onClick={next} className="btn-primary" data-testid="apply-next">
                {step.key === "intro" ? "Begin" : "Continue"} <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="apply-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Application <CheckCircle2 className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-muted-foreground mt-6">
          Powered by <span className="font-semibold">Leynix</span>
        </div>
      </div>
    </div>
  );
}

function Stepper({ currentIdx }) {
  return (
    <div className="flex items-center justify-between gap-1 max-w-2xl mx-auto" data-testid="stepper">
      {STEPS.map((s, i) => {
        const isCurrent = i === currentIdx;
        const isDone = i < currentIdx;
        return (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center text-center flex-1 min-w-0">
              <div
                className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center transition-colors ${
                  isCurrent || isDone ? "text-white" : "bg-secondary text-muted-foreground"
                }`}
                style={(isCurrent || isDone) ? { backgroundColor: "hsl(var(--primary))" } : {}}
              >
                {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <div className={`text-[10px] mt-1 hidden sm:block ${isCurrent ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                {s.label}
              </div>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < currentIdx ? "bg-foreground/30" : "bg-border"}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Intro({ agency }) {
  return (
    <div>
      <h2 className="heading-section">Welcome — let's get to know you.</h2>
      <p className="text-muted-foreground mt-2 text-sm">
        Thank you for your interest in joining <strong className="text-foreground">{agency.name}</strong>. This short application takes about 5 minutes. After submitting, you'll receive a tracking code to follow your application status.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <InfoCard icon={Briefcase} title="Quick" body="About 5 minutes" />
        <InfoCard icon={FileText} title="Secure" body="Documents encrypted" />
        <InfoCard icon={CheckCircle2} title="Trackable" body="Status updates by email" />
      </div>
    </div>
  );
}
function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-border rounded-lg p-3">
      <Icon className="w-4 h-4 mb-2 text-muted-foreground" />
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs text-muted-foreground">{body}</div>
    </div>
  );
}

function Input({ label, ...rest }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      <input className="input-base" {...rest} />
    </div>
  );
}

function PersonalStep({ data, set }) {
  return (
    <div>
      <h2 className="heading-section">About You</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-5">All fields marked are required.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="First name *" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="apply-first-name" />
        <Input label="Last name *" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="apply-last-name" />
        <Input label="Email *" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} required data-testid="apply-email" />
        <Input label="Phone *" type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} required data-testid="apply-phone" />
        <Input label="Street address" value={data.address} onChange={(e) => set("address", e.target.value)} />
        <Input label="City" value={data.city} onChange={(e) => set("city", e.target.value)} />
        <Input label="State" value={data.state} onChange={(e) => set("state", e.target.value)} />
        <Input label="ZIP" value={data.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
      </div>
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-sm font-medium mb-1.5">{label}</div>
      <div className="inline-flex bg-secondary rounded-lg p-1">
        {[
          { v: true, label: "Yes" },
          { v: false, label: "No" },
        ].map((opt) => (
          <button
            key={String(opt.v)}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${value === opt.v ? "bg-white shadow-sm font-medium" : "text-muted-foreground"}`}
            data-testid={`${testid}-${opt.v ? "yes" : "no"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function QualificationsStep({ data, set }) {
  return (
    <div>
      <h2 className="heading-section">Caregiver Qualifications</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-5">Tell us about your background.</p>
      <div className="space-y-5">
        <YesNo label="Do you have a valid driver's license?" value={data.has_drivers_license} onChange={(v) => set("has_drivers_license", v)} testid="q-license" />
        <YesNo label="Do you have reliable transportation?" value={data.has_reliable_transportation} onChange={(v) => set("has_reliable_transportation", v)} testid="q-transport" />
        <YesNo label="Are you a Certified Nursing Assistant (CNA)?" value={data.has_cna_certification} onChange={(v) => set("has_cna_certification", v)} testid="q-cna" />
        <YesNo label="Do you have current CPR certification?" value={data.has_cpr_certification} onChange={(v) => set("has_cpr_certification", v)} testid="q-cpr" />
        <YesNo label="Do you have a recent TB clearance?" value={data.has_tb_clearance} onChange={(v) => set("has_tb_clearance", v)} testid="q-tb" />
        <div>
          <label className="text-sm font-medium block mb-1.5">Years of caregiving experience</label>
          <select className="input-base" value={data.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} data-testid="q-experience">
            <option value="">Select…</option>
            <option>Less than 1 year</option>
            <option>1–2 years</option>
            <option>3–5 years</option>
            <option>6–10 years</option>
            <option>More than 10 years</option>
          </select>
        </div>
        <Input label="Most recent employer (optional)" value={data.previous_employer} onChange={(e) => set("previous_employer", e.target.value)} />
      </div>
    </div>
  );
}

function AvailabilityStep({ data, set, toggleDay }) {
  return (
    <div>
      <h2 className="heading-section">Availability</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-5">When are you available to work?</p>
      <div className="space-y-5">
        <div>
          <div className="text-sm font-medium mb-2">Available days</div>
          <div className="flex flex-wrap gap-2" data-testid="availability-days">
            {DAYS.map((d) => {
              const active = data.availability_days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${active ? "text-white border-transparent" : "border-border bg-card text-foreground hover:bg-secondary"}`}
                  style={active ? { backgroundColor: "hsl(var(--primary))" } : {}}
                  data-testid={`day-${d.toLowerCase()}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium block mb-1.5">Preferred shift</label>
          <select className="input-base" value={data.availability_shift} onChange={(e) => set("availability_shift", e.target.value)} data-testid="shift-select">
            <option value="">Select…</option>
            <option>Day (7am–3pm)</option>
            <option>Evening (3pm–11pm)</option>
            <option>Overnight (11pm–7am)</option>
            <option>Flexible</option>
          </select>
        </div>
        <Input label="Earliest start date" type="date" value={data.earliest_start_date} onChange={(e) => set("earliest_start_date", e.target.value)} />
        <div>
          <label className="text-sm font-medium block mb-1.5">Why do you want to work in home care?</label>
          <textarea className="input-base min-h-[100px]" value={data.why_home_care} onChange={(e) => set("why_home_care", e.target.value)} data-testid="q-why" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1.5">Anything else we should know?</label>
          <textarea className="input-base min-h-[80px]" value={data.additional_info} onChange={(e) => set("additional_info", e.target.value)} />
        </div>
      </div>
    </div>
  );
}

function DocumentsInfoStep() {
  return (
    <div>
      <h2 className="heading-section">Documents</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        After submitting your application, you'll be able to securely upload supporting documents. You can also indicate documents you'll bring to the office in person.
      </p>
      <div className="border border-dashed border-border rounded-xl p-5 bg-secondary/30">
        <div className="text-sm font-semibold mb-2">Documents we'll ask for:</div>
        <ul className="text-sm text-muted-foreground space-y-1">
          {DOC_TYPES.map((d) => (
            <li key={d.key} className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 opacity-50" /> {d.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-4 text-xs text-muted-foreground">
        Your documents are encrypted and only visible to the hiring team. We never share them with third parties.
      </div>
    </div>
  );
}

function ReviewStep({ data }) {
  const item = (label, val) => (
    <div className="flex justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <div className="text-muted-foreground text-sm">{label}</div>
      <div className="text-sm font-medium text-right">{val || "—"}</div>
    </div>
  );
  return (
    <div>
      <h2 className="heading-section">Review</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">Confirm your information before submitting.</p>
      <div className="space-y-1">
        {item("Name", `${data.first_name} ${data.last_name}`)}
        {item("Email", data.email)}
        {item("Phone", data.phone)}
        {item("Address", [data.address, data.city, data.state, data.zip_code].filter(Boolean).join(", "))}
        {item("CNA Certified", data.has_cna_certification === null ? "—" : data.has_cna_certification ? "Yes" : "No")}
        {item("CPR Certified", data.has_cpr_certification === null ? "—" : data.has_cpr_certification ? "Yes" : "No")}
        {item("Driver's License", data.has_drivers_license === null ? "—" : data.has_drivers_license ? "Yes" : "No")}
        {item("Reliable Transportation", data.has_reliable_transportation === null ? "—" : data.has_reliable_transportation ? "Yes" : "No")}
        {item("TB Clearance", data.has_tb_clearance === null ? "—" : data.has_tb_clearance ? "Yes" : "No")}
        {item("Experience", data.years_of_experience)}
        {item("Available days", data.availability_days.join(", "))}
        {item("Shift", data.availability_shift)}
        {item("Earliest start", data.earliest_start_date)}
      </div>
    </div>
  );
}

function SuccessAndUpload({ agency, submission, firstName }) {
  return (
    <div className="min-h-screen bg-[#F3F4F6] py-6 md:py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="surface p-6 md:p-10 text-center mb-6">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3" style={{ backgroundColor: "hsl(var(--primary) / 0.1)" }}>
            <CheckCircle2 className="w-8 h-8" style={{ color: "hsl(var(--primary))" }} />
          </div>
          <h1 className="heading-display">Application received</h1>
          <p className="text-muted-foreground mt-2">
            Thank you, <strong className="text-foreground">{firstName}</strong>. The {agency.name} team has been notified and will review your application.
          </p>
          <div className="mt-5 inline-flex flex-col items-center bg-secondary/60 rounded-lg px-5 py-3">
            <div className="overline">Your tracking code</div>
            <div className="font-mono text-xl font-semibold mt-1" data-testid="tracking-code">{submission.tracking_code}</div>
            <div className="text-xs text-muted-foreground mt-1">Save this. You'll need it to check your status.</div>
          </div>
          <div className="mt-6">
            <a href={`/track?code=${submission.tracking_code}`} className="btn-secondary inline-flex" data-testid="go-to-track">
              Track my application
            </a>
          </div>
        </div>

        <DocumentUploadSection applicationId={submission.id} trackingCode={submission.tracking_code} />
      </div>
    </div>
  );
}

function DocumentUploadSection({ applicationId, trackingCode }) {
  const [uploading, setUploading] = useState({});
  const [completed, setCompleted] = useState({});
  const [errors, setErrors] = useState({});

  const handleUpload = async (docType, file, willBring) => {
    setUploading((u) => ({ ...u, [docType]: true }));
    setErrors((e) => ({ ...e, [docType]: null }));
    try {
      const form = new FormData();
      form.append("document_type", docType);
      form.append("will_bring_to_office", willBring ? "true" : "false");
      if (file) form.append("file", file);
      await axios.post(`${API}/hiring/applications/${applicationId}/documents/public`, form, {
        headers: { "X-Tracking-Code": trackingCode, "Content-Type": "multipart/form-data" },
      });
      setCompleted((c) => ({ ...c, [docType]: willBring ? "will_bring" : (file?.name || "uploaded") }));
    } catch (e) {
      setErrors((er) => ({ ...er, [docType]: e?.response?.data?.detail || "Upload failed." }));
    } finally {
      setUploading((u) => ({ ...u, [docType]: false }));
    }
  };

  return (
    <div className="surface p-6 md:p-8">
      <h2 className="heading-section">Upload your documents</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-5">
        Upload as many as you can now — or check "I will bring this document to the office." You can return later using your tracking code.
      </p>
      <div className="space-y-3">
        {DOC_TYPES.map((doc) => (
          <DocRow
            key={doc.key}
            doc={doc}
            uploading={uploading[doc.key]}
            completed={completed[doc.key]}
            error={errors[doc.key]}
            onSelectFile={(f) => handleUpload(doc.key, f, false)}
            onWillBring={() => handleUpload(doc.key, null, true)}
          />
        ))}
      </div>
    </div>
  );
}

function DocRow({ doc, uploading, completed, error, onSelectFile, onWillBring }) {
  const inputRef = React.useRef();
  return (
    <div className="border border-border rounded-lg p-3 md:p-4" data-testid={`doc-row-${doc.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium text-sm">{doc.label}</div>
            {completed === "will_bring" && (
              <div className="text-xs text-amber-700">Will bring to office</div>
            )}
            {completed && completed !== "will_bring" && (
              <div className="text-xs text-emerald-700 truncate max-w-[200px]">Uploaded: {completed}</div>
            )}
            {error && <div className="text-xs text-red-600">{error}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.heic"
            onChange={(e) => e.target.files?.[0] && onSelectFile(e.target.files[0])}
            data-testid={`doc-file-${doc.key}`}
          />
          <button
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="btn-outline !px-3 !py-1.5 text-xs"
            data-testid={`doc-upload-${doc.key}`}
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5" /> Upload</>}
          </button>
          <button
            disabled={uploading}
            onClick={onWillBring}
            className="btn-ghost !px-3 !py-1.5 text-xs"
            data-testid={`doc-bring-${doc.key}`}
          >
            I'll bring it
          </button>
        </div>
      </div>
    </div>
  );
}
